<template>
  <div class="config-container" id="iframe-container">
    <iframe v-if="pdfBytes !== null" id="docViewerIframe" :src="pdfObjectUrl" :style="styleObj"></iframe>
  </div>
</template>

<script>
import { PDFDocument, rgb } from "pdf-lib";
import { mapGetters } from "vuex";
import nprogress from "nprogress";
export default {
  name: "DocumentViewer",
  props: ["documentUrl", "selectedItem", "success"],
  data: function() {
    return {
      pdfBytes: null,
      existingPdfBytes: null,
      originalPdfDoc: null,
      totalPages: 0,
      currentItem: this.selectedItem,
      successStatus: this.success,
      styleObj: {
        width: "100%",
        height: "730px",
        border: "none",
        backgroundColor: "#fff"
      }
    };
  },
  watch: {
    success(val) {
      this.successStatus = val === true;
      this.setUpSvgsOnPdf();
    },
    selectedItem(val) {
      this.currentItem = val;
      this.setUpSvgsOnPdf();
    },
    pdfBytes(val) {
      if (val !== null) {
        this.$emit("loading", false);
      }
    }
  },
  mounted() {
    this.setupPdf();
  },
  computed: {
    ...mapGetters(["user", "participant"]),
    pdfObjectUrl() {
      return this.pdfBytes !== null ? URL.createObjectURL(this.b64toBlob(this.pdfBytes, "application/pdf")) + "#toolbar=0&navpanes=0&scrollbar=0" : "";
    }
  },
  methods: {
    async setupPdf() {
      nprogress.start();
      this.$emit("loading", true);
      this.existingPdfBytes = await fetch(this.documentUrl).then(res => res.arrayBuffer());
      const originalPdfDoc = await PDFDocument.load(this.existingPdfBytes);
      const pages = originalPdfDoc.getPages();
      this.totalPages = pages.length;
      if (this.currentItem !== null) {
        this.setUpSvgsOnPdf();
      } else {
        const currentPage = pages[0];
        const pageSize = currentPage.getSize();
        this.setHeight(pageSize);
      }
      this.pdfBytes = await originalPdfDoc.saveAsBase64();
      this.$emit("loading", false);
      nprogress.done();
    },
    setHeight({ width, height }) {
      const offsetWidth = document.getElementById("iframe-container").clientWidth;
      this.styleObj.height = `${(height * offsetWidth) / width}px`;
    },
    async setUpSvgsOnPdf() {
      const originalPdfDoc = await PDFDocument.load(this.existingPdfBytes);
      if (this.currentItem === undefined || this.currentItem === null) {
        this.pdfBytes = await originalPdfDoc.saveAsBase64();
      } else {
        const pages = originalPdfDoc.getPages();
        const currentPage = pages[this.currentItem.pageNumber - 1];
        const pageSize = currentPage.getSize();
        this.setHeight(pageSize);
        const svgPath = this.createRoundedRectPath(...this.getSelectedActiveSvgParameters(currentPage.getWidth(), currentPage.getHeight()));
        currentPage.drawSvgPath(svgPath, {
          borderColor: rgb(1, 0, 0),
          borderWidth: 3,
          borderOpacity: 0.75
        });
        if (this.successStatus) {
          const tickMark = this.tickMarkSvgPath1(...this.getSelectedActiveSvgParameters(currentPage.getWidth(), currentPage.getHeight()));
          currentPage.drawSvgPath(tickMark, {
            borderColor: rgb(0.015, 0.666, 0.427),
            borderWidth: 3,
            borderOpacity: 1,
            color: rgb(0.015, 0.666, 0.427)
          });
        }
        this.pdfBytes = await originalPdfDoc.saveAsBase64();
      }
    },
    getSelectedActiveSvgParameters(width, height) {
      const newX = this.currentItem.xCoordinate;
      const newY = -height + this.currentItem.yCoordinate;
      return [newX, newY, this.currentItem.imageIconWidth, this.currentItem.imageIconHeight];
    },
    createRoundedRectPath(x, y, width, height, radius = 3) {
      const doubleRadius = radius * 2;
      return (
        // Move to position, offset by radius in x direction
        "M" +
        (x + radius) +
        "," +
        y +
        // Draw a horizontal line to the top right curve start
        "h" +
        (width - doubleRadius) +
        // Draw the top right corner curve
        "a" +
        radius +
        "," +
        radius +
        " 0 0 1 " +
        radius +
        "," +
        radius +
        // Draw a vertical line to the bottom right corner
        "v" +
        (height - doubleRadius) +
        // Draw the bottom right corner curve
        "a" +
        radius +
        "," +
        radius +
        " 0 0 1 " +
        -radius +
        "," +
        radius +
        // Draw a horizontal line to the bottom left corner
        "h" +
        (doubleRadius - width) +
        // Draw the bottom left corner
        "a" +
        -radius +
        "," +
        -radius +
        " 0 0 1 " +
        -radius +
        "," +
        -radius +
        // Draw a vertical line to the top left corner
        "v" +
        (doubleRadius - height) +
        // Draw the top left corner
        "a" +
        radius +
        "," +
        -radius +
        " 0 0 1 " +
        radius +
        "," +
        -radius +
        // Close the shape
        "z"
      );
    },
    tickMarkSvgPath(x, y, width) {
      return "M" + (x + width + 12) + " " + y + " " + "c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm-1.25 17.292l-4.5-4.364 1.857-1.858 2.643 2.506 5.643-5.784 1.857 1.857-7.5 7.643z";
    },
    tickMarkSvgPath1(x, y, width) {
      const path = "M" + (x + width) + " " + (y - 12) + " " + "c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0-2c-6.627 0-12 5.373-12 12s5.373 12 12 12 12-5.373 12-12-5.373-12-12-12zm4.393 7.5l-5.643 5.784-2.644-2.506-1.856 1.858 4.5 4.364 7.5-7.643-1.857-1.857z";
      return path;
    },
    b64toBlob(b64Data, contentType) {
      const byteCharacters = atob(b64Data);
      const byteArrays = [];
      for (let offset = 0; offset < byteCharacters.length; offset += 512) {
        const slice = byteCharacters.slice(offset, offset + 512);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
      }
      const blob = new Blob(byteArrays, { type: contentType });
      return blob;
    }
  }
};
</script>
<style scoped>
viewer-toolbar,
viewer-pdf-sidenav,
viewer-toolbar {
  display: none;
}
#loadingMessage {
  width: 100%;
  height: 100%;
  z-index: 1000;
  background: #ccc;
  top: 0px;
  left: 0px;
  position: absolute;
}
</style>
